// Encapsulate axios in a service
import Axios, {
	AxiosError,
	AxiosHeaders,
	AxiosInstance,
	AxiosRequestConfig,
	AxiosRequestHeaders,
	AxiosResponse,
} from "axios"
import NProgress from "nprogress"
import jwtDecode from "jwt-decode"
import { localStorageService, sessionStorageService, STORAGE_KEY } from "services/browserStorage"

import i18next from "i18next"
import { ErrorResponse } from "models"
import { AlertFnType } from "hooks"

export const API_BASE_URL_TEST = "https://dev.ocode.team/"
export const API_BASE_URL_PROD = "/"
const API_TIMEOUT_GET = 30000

const headers: Readonly<Record<string, string | boolean>> = {
	Accept: "application/json",
	"Content-Type": "application/json; charset=utf-8",
	"X-Requested-With": "XMLHttpRequest",
	"Accept-Version": "1.0.0",
	// 'Content-Encoding': 'gzip',
}

Axios.defaults.headers.common = {
	...headers,
}

type localConfig = {
	token?: string
	refreshToken?: string
}

type decodedToken = {
	name: string
	exp: number
}

type GetTokenProps = {
	logout: () => void
	alert: AlertFnType
}

class Http {
	// Axios instance
	private instance: AxiosInstance | null = null
	private get http(): AxiosInstance {
		return this.instance
	}

	// Config
	private config: localConfig = {}

	// Progress bar
	private setProgressBar = () => NProgress.start()
	private hideProgressBar = () => NProgress.done(true)

	// INIT --------

	initHttp({ logout, alert }: { logout: () => void; alert: AlertFnType }): AxiosInstance {
		// Init config from local storage.
		this.config = this.loadConfig()

		// Create new instance
		const http = Axios.create({
			baseURL:
				process.env.REACT_APP_MODE === "production" ? API_BASE_URL_PROD : API_BASE_URL_TEST,
			timeout: API_TIMEOUT_GET,
		})

		// Intercept the request to make sure the existing token is injected into the header
		http.interceptors.request.use(async (config) => {
			this.setProgressBar()
			// Use fresh token
			if (!(config.headers as AxiosRequestHeaders)?.Authorization && this.config.token) {
				try {
					const token = await this.getToken({ logout, alert })
					if (token)
						(config.headers as AxiosHeaders).set("Authorization", `Bearer ${token}`)
				} catch (error) {
					console.error("initHttp", error)
					// authentification timeout
					logout()
					alert("error", i18next.t("sell_error_AccessTokenRequired", { ns: "api" }))
				}
			}
			return config
		})

		// Intercept the response and...
		http.interceptors.response.use(
			(response) => {
				this.hideProgressBar()
				// ...get the tokens if they exists and save them
				const token = response.headers.Authorization || response.data.access_token
				const refreshToken = response.data.refresh_token
				// Update config object if a token is updated
				if ((token && token !== this.config.token) || refreshToken) {
					this.config = {
						...this.config,
						token: token || this.config.token,
						refreshToken: refreshToken || this.config.refreshToken,
					}
					this.saveConfig()
				}
				return response
			},
			(error) => {
				this.hideProgressBar()
				//console.error(error)
				return Promise.reject(error)
			}
		)

		this.instance = http
		return http
	}

	// METHODS --------

	send<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
		return this.http?.request(config)
	}

	get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
		return this.http?.get<T, R>(url, config)
	}

	post<T = any, R = AxiosResponse<T>>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<R> {
		return this.http?.post<T, R>(url, data, config)
	}

	put<T = any, R = AxiosResponse<T>>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<R> {
		return this.http?.put<T, R>(url, data, config)
	}

	patch<T = any, R = AxiosResponse<T>>(
		url: string,
		data?: any,
		config?: AxiosRequestConfig
	): Promise<R> {
		return this.http?.put<T, R>(url, data, config)
	}

	delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
		return this.http?.delete<T, R>(url, config)
	}

	// CONFIG MANAGEMENT --------

	// Save config to local storage.
	private saveConfig(externalToken?: string) {
		if (externalToken) {
			// override config with given token
			this.config = {
				...this.config,
				token: externalToken,
				refreshToken: null,
			}
			// externalToken is saved in session storage
			sessionStorageService.set(STORAGE_KEY, JSON.stringify(this.config))
			// console.log("saved config in session:", this.config)
			return
		}

		// @TODO: JWT should not be stored in local storage for XSS reason ?
		// See if we can set it from an httponly session cookie ?
		localStorageService.set(STORAGE_KEY, JSON.stringify(this.config))
		// console.log("saved config in local:", this.config)
	}

	//Update Accesss token in to local storage
	private updateAccesTokenIntoLocalStorage(externalToken?: string) {
		if (!externalToken) throw new Error("Error token")
		this.config = {
			...this.config,
			token: externalToken,
		}
		// externalToken is saved in session storage
		sessionStorageService.set(STORAGE_KEY, JSON.stringify(this.config))
	}

	// Load initial config value from local storage.
	private loadConfig() {
		let _config
		try {
			_config = JSON.parse(localStorageService.get(STORAGE_KEY))
			// check if BO is connected as partner. If the page is refresh, keep the right token
			const BOConnectedAsPartner = sessionStorageService.get(STORAGE_KEY)
			if (BOConnectedAsPartner) {
				_config = { ..._config, ...JSON.parse(BOConnectedAsPartner) }
			}
		} catch (_error) {
			_config = {}
		}
		return { ...this.config, ..._config }
	}

	public clearConfig() {
		this.config.token = null
		localStorageService.remove(STORAGE_KEY)
		sessionStorageService.remove(STORAGE_KEY)
	}

	public clearSupportSession() {
		this.config.token = null
		sessionStorageService.remove(STORAGE_KEY)
		// restore actual session
		this.config = this.loadConfig()
	}

	// TOKEN MANAGEMENT --------

	// Check if token is expired
	private isTokenExpired() {
		if (!this.config.token) return true
		try {
			const decoded = jwtDecode<decodedToken>(this.config.token)
			return new Date() >= new Date(decoded.exp * 1000)
		} catch (e) {
			return true
		}
	}

	// Get new access token from backend
	private async _refreshToken() {
		if (!this.config.refreshToken) throw new Error("No refresh token")
		// token in the response will be intercepted
		// Note that if the refreshToken is invalid (not tested), then the token will remain null,
		// the user will have to manually reset all tokens (ie login)
		// console.log(" GET NEW ACCESS TOKEN GET NEW ACCESS TOKEN GET NEW ACCESS TOKEN")
		await this.instance.request({
			url: "obikeapi/entity/signin",
			data: {},
			method: "GET",
			headers: { Authorization: `Bearer ${this.config.refreshToken}` },
		})
	}

	async signinWithRefreshTokenForBikeFleet({ logout, alert }) {
		if (!this.config.refreshToken)
			throw new Error(i18next.t("bikeFleetErrorBoSigninWithRefresh", { ns: "api" }))
		try {
			const response = await this.instance.request({
				url: "obikeapi/entity/signin",
				data: {},
				method: "GET",
				headers: { Authorization: `Bearer ${this.config.refreshToken}` },
			})
			this.updateAccesTokenIntoLocalStorage(response.data.access_token)
		} catch (error) {
			if (logout) logout()
			if (alert) alert("error", i18next.t("bikeFleetErrorBoSigninWithRefresh", { ns: "api" }))
		}
	}

	// If token expired, but we have a refreshToken, get fresh token
	async getToken({ logout, alert }: GetTokenProps) {
		if (this.isTokenExpired()) {
			try {
				// If token is invalid, clear it, then refresh it
				this.config.token = null
				// @ts-ignore TODO: remove ts-ignore
				await this._refreshToken()
			} catch (error) {
				//console.log("getToken: ", error)
				// authentification timeout
				if (logout) logout()
				if (alert)
					alert("error", i18next.t("sell_error_AccessTokenRequired", { ns: "api" }))
			}
		}
		return this.config.token
	}
}

export const http = new Http()

export const catchError = (error: AxiosError): { error: ErrorResponse } | null =>
	error?.response?.data ? { error: error?.response?.data as ErrorResponse } : null

export type ErrorFn = (error: ErrorResponse) => ErrorResponse | void
export type GetError<T = any> = (
	res: T | ErrorResponse,
	errorFn: ErrorFn
) => () => ErrorResponse | void

export const getError: GetError = (res, errorFn) => () =>
	!res || res?.error ? errorFn(res?.error) : null
